import api from '../../utils/api';
import type {
  DeepLinkAnalyticsSummary,
  DeepLinkBlogSeedList,
  DeepLinkCampaign,
  DeepLinkCampaignAnalyticsDetail,
  DeepLinkCampaignAnalyticsRow,
  DeepLinkCampaignFormValues,
  DeepLinkCampaignLeaderboardRow,
  DeepLinkConsultationMode,
  DeepLinkDoctorSeedList,
  DeepLinkFunnel,
  DeepLinkRoute,
  DeepLinkRouteFormValues,
  DeepLinkScreenCatalog,
  DeepLinkScreenCatalogItem,
  DeepLinkEnvironmentConfig,
  DeepLinkTargetAppKey,
  DeepLinkShortCodeLookup,
} from '../../models/MasterAdmin/deepLink';

const ROUTES_BASE = '/master-admin/deep-links/routes';
const CAMPAIGNS_BASE = '/master-admin/deep-links/campaigns';
const ANALYTICS_BASE = '/master-admin/deep-links/analytics';

type ApiList<T> = { success: boolean; data: T };
type ApiOne<T> = { success: boolean; data: T };

export async function fetchDeepLinkRoutes(includeInactive = true): Promise<DeepLinkRoute[]> {
  const res = await api.get<ApiList<DeepLinkRoute[]>>(ROUTES_BASE, {
    params: { includeInactive },
  });
  return res.data.data || [];
}

export async function createDeepLinkRoute(payload: DeepLinkRouteFormValues): Promise<DeepLinkRoute> {
  const res = await api.post<ApiOne<DeepLinkRoute>>(ROUTES_BASE, payload);
  return res.data.data;
}

export async function updateDeepLinkRoute(
  id: string,
  payload: Partial<DeepLinkRouteFormValues>
): Promise<DeepLinkRoute> {
  const res = await api.put<ApiOne<DeepLinkRoute>>(`${ROUTES_BASE}/${id}`, payload);
  return res.data.data;
}

export async function deleteDeepLinkRoute(id: string): Promise<void> {
  await api.delete(`${ROUTES_BASE}/${id}`);
}

export async function seedDefaultDeepLinkRoutes(): Promise<{ created: number; updated: number; total?: number }> {
  const res = await api.post<ApiOne<{ created: number; updated: number; total?: number }>>(
    `${ROUTES_BASE}/seed-defaults`
  );
  return res.data.data;
}

export async function syncDeepLinkScreenRegistry(): Promise<{
  created: number;
  updated: number;
  skipped?: number;
  errors?: { screenName: string; message: string }[];
}> {
  const res = await api.post<
    ApiOne<{
      created: number;
      updated: number;
      skipped?: number;
      errors?: { screenName: string; message: string }[];
    }>
  >(`${ROUTES_BASE}/sync-screen-registry`);
  return res.data.data;
}

export async function fetchDeepLinkScreenCatalog(
  targetAppKey: DeepLinkTargetAppKey = 'user'
): Promise<DeepLinkScreenCatalog> {
  const res = await api.get<ApiOne<DeepLinkScreenCatalog>>('/master-admin/deep-links/screen-catalog', {
    params: { targetAppKey },
  });
  return res.data.data;
}

export async function ensureDeepLinkRouteFromScreen(screenName: string): Promise<DeepLinkRoute> {
  const res = await api.post<ApiOne<DeepLinkRoute>>(`${ROUTES_BASE}/from-screen`, { screenName });
  return res.data.data;
}

export type { DeepLinkScreenCatalogItem };

export async function fetchDeepLinkConfig(): Promise<DeepLinkEnvironmentConfig> {
  const res = await api.get<ApiOne<DeepLinkEnvironmentConfig>>('/master-admin/deep-links/config');
  return res.data.data;
}

export async function fetchDeepLinkCampaigns(params?: {
  status?: string;
  search?: string;
  routeSlug?: string;
  targetAppKey?: string;
  entityType?: string;
  excludeEntityTypes?: string;
  limit?: number;
  offset?: number;
}): Promise<DeepLinkCampaign[]> {
  const res = await api.get<ApiList<DeepLinkCampaign[]> & { meta?: { total?: number } }>(
    CAMPAIGNS_BASE,
    {
      params: {
        limit: 2000,
        ...params,
      },
    }
  );
  return res.data.data || [];
}

export async function fetchDeepLinkCampaignsPage(params?: {
  status?: string;
  search?: string;
  routeSlug?: string;
  targetAppKey?: string;
  entityType?: string;
  excludeEntityTypes?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: DeepLinkCampaign[]; total: number; limit: number; offset: number }> {
  const res = await api.get<
    ApiList<DeepLinkCampaign[]> & { meta?: { total?: number; limit?: number; offset?: number } }
  >(CAMPAIGNS_BASE, {
    params: {
      limit: 2000,
      ...params,
    },
  });
  return {
    items: res.data.data || [],
    total: res.data.meta?.total ?? (res.data.data || []).length,
    limit: res.data.meta?.limit ?? params?.limit ?? 2000,
    offset: res.data.meta?.offset ?? params?.offset ?? 0,
  };
}

export async function fetchDeepLinkCampaign(id: string): Promise<DeepLinkCampaign> {
  const res = await api.get<ApiOne<DeepLinkCampaign>>(`${CAMPAIGNS_BASE}/${id}`);
  return res.data.data;
}

export async function lookupDeepLinkShortCode(
  shortCode: string
): Promise<DeepLinkShortCodeLookup> {
  const code = String(shortCode || '').trim();
  const res = await api.get<ApiOne<DeepLinkShortCodeLookup>>(
    '/master-admin/deep-links/lookup',
    { params: { q: code } }
  );
  return res.data.data;
}

export async function createDeepLinkCampaign(
  payload: DeepLinkCampaignFormValues
): Promise<DeepLinkCampaign> {
  const res = await api.post<ApiOne<DeepLinkCampaign>>(CAMPAIGNS_BASE, payload);
  return res.data.data;
}

export async function updateDeepLinkCampaign(
  id: string,
  payload: Partial<DeepLinkCampaignFormValues>
): Promise<DeepLinkCampaign> {
  const res = await api.put<ApiOne<DeepLinkCampaign>>(`${CAMPAIGNS_BASE}/${id}`, payload);
  return res.data.data;
}

export async function deleteDeepLinkCampaign(id: string, options?: { force?: boolean }): Promise<void> {
  await api.delete(`${CAMPAIGNS_BASE}/${id}`, {
    params: options?.force ? { force: 'true' } : undefined,
  });
}

export async function activateDeepLinkCampaign(id: string): Promise<DeepLinkCampaign> {
  const res = await api.post<ApiOne<DeepLinkCampaign>>(`${CAMPAIGNS_BASE}/${id}/activate`);
  return res.data.data;
}

export async function pauseDeepLinkCampaign(id: string): Promise<DeepLinkCampaign> {
  const res = await api.post<ApiOne<DeepLinkCampaign>>(`${CAMPAIGNS_BASE}/${id}/pause`);
  return res.data.data;
}

export async function regenerateDeepLinkCampaignLinks(id: string): Promise<DeepLinkCampaign> {
  const res = await api.post<ApiOne<DeepLinkCampaign>>(`${CAMPAIGNS_BASE}/${id}/regenerate-links`);
  return res.data.data;
}

export async function fetchDeepLinkCampaignQr(id: string): Promise<{
  qrUrl: string;
  targetUrl: string;
  shortUrl?: string;
  fullUrl?: string;
}> {
  const res = await api.get<
    ApiOne<{ qrUrl: string; targetUrl: string; shortUrl?: string; fullUrl?: string }>
  >(`${CAMPAIGNS_BASE}/${id}/qr`);
  return res.data.data;
}

export async function previewDeepLink(payload: {
  routeId: string;
  routeParams?: Record<string, string>;
  loginRequired?: boolean;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
}): Promise<{ fullUrl: string; webFallbackUrl?: string; route: DeepLinkRoute }> {
  const res = await api.post<
    ApiOne<{ fullUrl: string; webFallbackUrl?: string; route: DeepLinkRoute }>
  >(`${CAMPAIGNS_BASE}/preview`, payload);
  return res.data.data;
}

export async function fetchDeepLinkAnalyticsSummary(params?: {
  from?: string;
  to?: string;
  campaignId?: string;
}): Promise<DeepLinkAnalyticsSummary> {
  const res = await api.get<ApiOne<DeepLinkAnalyticsSummary>>(`${ANALYTICS_BASE}/summary`, {
    params,
  });
  return res.data.data;
}

export async function fetchDeepLinkFunnel(params?: {
  from?: string;
  to?: string;
  campaignId?: string;
}): Promise<DeepLinkFunnel> {
  const res = await api.get<ApiOne<DeepLinkFunnel>>(`${ANALYTICS_BASE}/funnel`, { params });
  return res.data.data;
}

export async function fetchDeepLinkLeaderboard(params?: {
  from?: string;
  to?: string;
  limit?: number;
}): Promise<DeepLinkCampaignLeaderboardRow[]> {
  const res = await api.get<ApiList<DeepLinkCampaignLeaderboardRow[]>>(
    `${ANALYTICS_BASE}/leaderboard`,
    { params }
  );
  return res.data.data || [];
}

export async function fetchDeepLinkDailySeries(params?: {
  from?: string;
  to?: string;
  campaignId?: string;
}): Promise<{ clicks: { day: string; clicks: number }[]; conversions: unknown[] }> {
  const res = await api.get<
    ApiOne<{ clicks: { day: string; clicks: number }[]; conversions: unknown[] }>
  >(`${ANALYTICS_BASE}/daily`, { params });
  return res.data.data;
}

export async function fetchDeepLinkAnalyticsByCampaign(params?: {
  from?: string;
  to?: string;
}): Promise<Record<string, DeepLinkCampaignAnalyticsRow>> {
  const res = await api.get<ApiOne<Record<string, DeepLinkCampaignAnalyticsRow>>>(
    `${ANALYTICS_BASE}/by-campaign`,
    { params }
  );
  return res.data.data || {};
}

export async function fetchDeepLinkCampaignAnalyticsDetail(
  campaignId: string,
  params?: { from?: string; to?: string; limit?: number }
): Promise<DeepLinkCampaignAnalyticsDetail> {
  const res = await api.get<ApiOne<DeepLinkCampaignAnalyticsDetail>>(
    `${ANALYTICS_BASE}/campaign/${campaignId}`,
    { params }
  );
  return res.data.data;
}

const SEED_BASE = '/master-admin/deep-links/seed';

export async function fetchDeepLinkDoctorSeedList(params?: {
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<DeepLinkDoctorSeedList> {
  const res = await api.get<ApiOne<DeepLinkDoctorSeedList>>(`${SEED_BASE}/doctors`, { params });
  return res.data.data;
}

export async function seedDeepLinkDoctor(
  vendorId: string,
  modes?: DeepLinkConsultationMode[]
): Promise<{
  vendorId: string;
  doctorName: string;
  created: unknown[];
  skipped: unknown[];
}> {
  const res = await api.post<
    ApiOne<{ vendorId: string; doctorName: string; created: unknown[]; skipped: unknown[] }>
  >(`${SEED_BASE}/doctors/${vendorId}`, { modes });
  return res.data.data;
}

export async function seedAllDeepLinkDoctors(modes?: DeepLinkConsultationMode[]): Promise<{
  doctorCount: number;
  doctorsTouched: number;
  createdCount: number;
  skippedCount: number;
  errorCount: number;
  errors: Array<{ vendorId: string; doctorName?: string | null; message: string }>;
}> {
  const res = await api.post<
    ApiOne<{
      doctorCount: number;
      doctorsTouched: number;
      createdCount: number;
      skippedCount: number;
      errorCount: number;
      errors: Array<{ vendorId: string; doctorName?: string | null; message: string }>;
    }>
  >(`${SEED_BASE}/doctors`, { modes });
  return res.data.data;
}

export async function fetchDeepLinkBlogSeedList(params?: {
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<DeepLinkBlogSeedList> {
  const res = await api.get<ApiOne<DeepLinkBlogSeedList>>(`${SEED_BASE}/blogs`, { params });
  return res.data.data;
}

export async function seedDeepLinkBlogs(blogIds?: string[]): Promise<{
  created: unknown[];
  skipped: unknown[];
  createdCount: number;
  skippedCount: number;
}> {
  const res = await api.post<
    ApiOne<{
      created: unknown[];
      skipped: unknown[];
      createdCount: number;
      skippedCount: number;
    }>
  >(`${SEED_BASE}/blogs`, { blogIds });
  return res.data.data;
}
